import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { GetMultiSelectClients } from "../../../services/ClientsMaintenanceService";
import CommonLoader from "../../common/CommonLoader";
import { NotificationManager } from "react-notifications";

const UserRouteMap = ({
  accounts,
  onAccountsOrderChange,
  isForView,
  clientStops,
  onClientStopsChange,
}) => {
  const [route, setRoute] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogAccount, setNoteDialogAccount] = useState(null);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (accounts) {
      const accountList =
        typeof accounts === "string"
          ? accounts.split(",").map((a) => a.trim()).filter(Boolean)
          : Array.isArray(accounts)
            ? accounts
            : [];
      setRoute(["Lab", ...accountList, "Lab"]);
    }
  }, [accounts]);

  useEffect(() => {
    fetchData();
  }, []);

  const getSpecialNote = (accountNumber) => {
    if (!clientStops) return "";
    const stop = clientStops.find((s) => s.clientAccountNumber === accountNumber);
    return stop ? stop.specialNotes || "" : "";
  };

  const onDragEnd = (result) => {
    if (isForView) return;
    const { source, destination } = result;
    if (
      !destination ||
      destination.index === 0 ||
      destination.index === route.length - 1
    )
      return;

    const updatedRoute = Array.from(route);
    const [moved] = updatedRoute.splice(source.index, 1);
    updatedRoute.splice(destination.index, 0, moved);
    setRoute(updatedRoute);

    const newOrder = updatedRoute.slice(1, -1).join(",");
    onAccountsOrderChange(newOrder);

    if (onClientStopsChange && clientStops) {
      const reorderedAccounts = updatedRoute.slice(1, -1);
      const updatedStops = reorderedAccounts.map((acc, idx) => {
        const existing = clientStops.find((s) => s.clientAccountNumber === acc);
        return {
          ...(existing || {}),
          clientAccountNumber: acc,
          stopNumber: idx + 1,
          specialNotes: existing ? existing.specialNotes || "" : "",
        };
      });
      onClientStopsChange(updatedStops);
    }
  };

  const getClientDisplayName = (accountNumber) => {
    const client = clients.find((c) => c.accountNumber === accountNumber);
    return client ? `${accountNumber} - ${client.clientName}` : accountNumber;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await GetMultiSelectClients("");
      const clientsData = response.results.map((client) => ({
        accountNumber: client.accountNumber.trim(),
        clientName: client.clientName.trim(),
      }));
      setClients(clientsData);
      setLoading(false);
    } catch (error) {
      NotificationManager.error("Error fetching clients:", error);
      setLoading(false);
    }
  };

  const handleStopClick = (accountNumber) => {
    if (isForView) return;
    setNoteDialogAccount(accountNumber);
    setNoteText(getSpecialNote(accountNumber));
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (!onClientStopsChange || !clientStops) {
      setNoteDialogOpen(false);
      return;
    }
    const routeAccounts = route.slice(1, -1);
    const updatedStops = routeAccounts.map((acc, idx) => {
      const existing = clientStops.find((s) => s.clientAccountNumber === acc);
      return {
        ...(existing || {}),
        clientAccountNumber: acc,
        stopNumber: idx + 1,
        specialNotes:
          acc === noteDialogAccount
            ? noteText
            : existing
              ? existing.specialNotes || ""
              : "",
      };
    });
    onClientStopsChange(updatedStops);
    setNoteDialogOpen(false);
  };

  if (route.length === 0) return null;

  return (
    <>
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <Typography variant="h5" className="title">
          Route for Rider
        </Typography>
        <DragDropContext onDragEnd={isForView ? undefined : onDragEnd}>
          <Droppable droppableId="route" direction="vertical">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                {route.map((stop, index) =>
                  stop === "Lab" ? (
                    <Box
                      key={stop + index}
                      sx={{
                        padding: "12px 24px",
                        backgroundColor: "#4CAF50",
                        color: "#fff",
                        border: "2px solid #2E7D32",
                        borderRadius: "12px",
                        fontWeight: "bold",
                        fontSize: "18px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minWidth: "60px",
                        textAlign: "center",
                        position: "relative",
                        "&:after": {
                          content: '""',
                          position: "absolute",
                          left: "50%",
                          bottom: "-10px",
                          transform: "translateX(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft:
                            index === route.length - 1
                              ? "none"
                              : "10px solid transparent",
                          borderRight:
                            index === route.length - 1
                              ? "none"
                              : "10px solid transparent",
                          borderTop:
                            index === route.length - 1
                              ? "none"
                              : "10px solid #4CAF50",
                        },
                      }}
                    >
                      {stop}
                    </Box>
                  ) : (
                    <Draggable
                      key={stop + index}
                      draggableId={stop + index}
                      index={index}
                      isDragDisabled={isForView}
                    >
                      {(provided, snapshot) => {
                        const hasNote = !!getSpecialNote(stop);
                        const notePreview = getSpecialNote(stop);

                        return (
                          <Tooltip
                            title={
                              isForView
                                ? notePreview || "No special notes"
                                : hasNote
                                  ? `Note: ${notePreview} — Click to edit`
                                  : "Click to add a special note · Drag to reorder"
                            }
                            arrow
                          >
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => handleStopClick(stop)}
                              sx={{
                                padding: "10px 20px",
                                backgroundColor: snapshot.isDragging
                                  ? "#64b5f6"
                                  : hasNote
                                    ? "#1565C0"   // darker blue when note exists
                                    : "#2196F3",
                                color: "#fff",
                                borderRadius: "8px",
                                fontWeight: "bold",
                                cursor: isForView ? "default" : "pointer",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                minWidth: "280px",
                                position: "relative",
                                border: hasNote
                                  ? "2px solid #fff176"  // yellow border when note exists
                                  : "2px solid transparent",
                                transition: "background-color 0.2s, border 0.2s",
                                "&:hover": isForView
                                  ? {}
                                  : {
                                    backgroundColor: snapshot.isDragging
                                      ? "#64b5f6"
                                      : "#1976D2",
                                  },
                                // Arrow connector
                                "&:after": {
                                  content: '""',
                                  position: "absolute",
                                  left: "50%",
                                  bottom: "-10px",
                                  transform: "translateX(-50%)",
                                  width: 0,
                                  height: 0,
                                  borderLeft:
                                    index === route.length - 1
                                      ? "none"
                                      : "10px solid transparent",
                                  borderRight:
                                    index === route.length - 1
                                      ? "none"
                                      : "10px solid transparent",
                                  borderTop:
                                    index === route.length - 1
                                      ? "none"
                                      : "10px solid #2196F3",
                                },
                              }}
                            >
                              {/* Top row: stop number + client name */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  width: "100%",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    backgroundColor: "rgba(0,0,0,0.25)",
                                    borderRadius: "50%",
                                    width: 22,
                                    height: 22,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {index}
                                </Typography>
                                <Typography sx={{ flexGrow: 1, textAlign: "left" }}>
                                  {getClientDisplayName(stop)}
                                </Typography>
                              </Box>

                              {/* Note preview row — only shown if note exists */}
                              {hasNote && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 0.5,
                                    pl: "30px", // align under client name
                                    color: "#fff176",
                                    fontWeight: "normal",
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "100%",
                                  }}
                                >
                                  📝 {notePreview}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      }}
                    </Draggable>
                  )
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Special Notes Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Special Note
          <Typography variant="body2" color="text.secondary">
            {noteDialogAccount && getClientDisplayName(noteDialogAccount)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            size="small"
            placeholder="Enter any special instructions for this stop..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          {/* Allow clearing the note */}
          <Button
            onClick={() => setNoteText("")}
            color="error"
            sx={{ mr: "auto" }}
            size="small"
          >
            Clear Note
          </Button>
          <Button onClick={() => setNoteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNote}
            color="primary"
            variant="contained"
            startIcon={<CheckCircleIcon />}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      <CommonLoader loading={loading} />
    </>
  );
};

export default UserRouteMap;